import type { ReactNode } from 'react';
import { EmptyState } from '@/shared/components/ui/EmptyState';

interface PanelStateProps {
  isLoading: boolean;
  isError: boolean;
  /** The server's honest `empty` flag — drives the explicit "No data" state. */
  isEmpty: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: ReactNode;
  children: ReactNode;
}

/**
 * Standard loading / error / empty gate for an analytics panel.
 *
 * The empty branch renders an explicit "No data" state driven by the server's
 * `empty` flag — it NEVER shows fabricated or placeholder numbers (Req 14.1,
 * 14.2). Only when data genuinely exists are `children` rendered.
 */
export function PanelState({
  isLoading,
  isError,
  isEmpty,
  emptyTitle = 'Nothing to report yet',
  emptyDescription = 'No rows match the current selection. Widen the period or clear a filter.',
  emptyIcon,
  children,
}: PanelStateProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 p-1" role="status" aria-busy="true" aria-live="polite">
        <span className="sr-only">Loading this panel</span>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[74px] animate-pulse rounded-card border border-border bg-card">
              <div className="m-3 h-4 w-1/2 animate-pulse rounded bg-surface-container" />
              <div className="mx-3 h-3 w-2/3 animate-pulse rounded bg-surface-container" />
            </div>
          ))}
        </div>
        <div className="h-40 animate-pulse rounded-card border border-border bg-card" />
      </div>
    );
  }
  if (isError) {
    return (
      <EmptyState
        title="This panel could not be read"
        description="The analytics service did not answer for the current selection. Retry, and report it if it persists."
      />
    );
  }
  if (isEmpty) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />;
  }
  return <>{children}</>;
}
